import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import copy from 'copy-to-clipboard';
import clsx from 'clsx';
import MUITooltip from '@material-ui/core/Tooltip';

import { Label } from 'core';
import { useStore } from 'core/store';
import { brandConstants } from 'brand-config';
import { useToast } from 'core/components/toast';
import { FormField } from 'core/components/form';
import { Card } from 'core/components/card';
import { NotificationCard } from 'core/components/notification-card';
import rightArrow from 'core/assets/svg/ic-right-arrow.svg';
import openInTab from 'core/assets/svg/open-tab.svg';
import infoIcon from 'core/assets/svg/info-icon-dark.svg';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { CardAnchorLink } from './anchor-link';

import useStyles from './changes-for-approval-section.styles';

const ProofOfElegibilityDocumentsChange = ({
  isProcessingView = false,
  goToPIPCoverageSection,
  openMemberDocuments
}) => {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      policies: {
        policy: {
          policy: { id }
        }
      }
    }
  } = useStore();

  const { isAgency, user } = useSession();

  const copyEmailSubject = () => {
    const emailSubject = `Michigan PIP proof-of-eligibility documents for policy ${id}`;
    const copied = copy(emailSubject);

    if (copied) {
      toast.notify({
        type: 'success',
        message: 'The email subject line was copied to your clipboard.'
      });

      if (isAgency) {
        track('Change request - Copy email subject line', {
          isAgency,
          username: user.username
        });
      }
    }
  };

  if (!isAgency && !isProcessingView) {
    return (
      <Card type="primary" className={classes.pipChangeContainer}>
        <Label type="darkGreenSmallBold">Proof-of-eligibility documents</Label>
        <p className={classes.pipChangeDescription}>
          Please make sure the policy will be eligible for the desired PIP Medical limit, and gather any new
          proof-of-eligibility documents from the member, if needed.
        </p>

        <div className={classes.linkSection}>
          <CardAnchorLink
            label="See PIP coverage options and eligibility requirements"
            onClick={goToPIPCoverageSection}
            icon={rightArrow}
            classes={classes}
            iconClass={classes.arrowIcon}
          />
          <CardAnchorLink
            label="See member's documents"
            icon={openInTab}
            classes={classes}
            onClick={openMemberDocuments}
          />
        </div>

        <NotificationCard type="secondary" icon={infoIcon} iconSize="icon16" className={classes.infoBox}>
          <div>
            Please upload any required new proof-of-eligibility documents to the member’s internal documents before
            submitting for approval.
          </div>
        </NotificationCard>
        <FormField
          mode="light"
          label="I confirm that I have uploaded the required proof of eligibility documents or have verified the existing ones are valid."
          type="checkbox"
          id="proofOfElegibilityDocumentsAttestation"
          name="proofOfElegibilityDocumentsAttestation"
          ignoreGlobalDisabledState
          topAlignCheckbox
        />
      </Card>
    );
  }

  if (isProcessingView) {
    return (
      <Card type="primary" className={clsx(classes.pipChangeContainer, classes.pipChangeProcessingUserContainer)}>
        <img src={infoIcon} alt="info" />
        <div>
          <Label type="darkGreenSmallBold">
            Check your processing inbox for additional proof-of-eligibility documents.
          </Label>
          <p className={classes.pipChangeDescription}>
            If this change requires new proof of eligibility documents, the agent may have emailed additional documents
            to the processing team’s inbox using this subject line:{' '}
            <MUITooltip title="Click to copy to clipboard" arrow placement="top" classes={{ tooltip: classes.tooltip }}>
              <span onClick={copyEmailSubject} className={classes.emailLink}>
                Michigan PIP proof-of-eligibility documents for policy {id}
              </span>
            </MUITooltip>
            . If valid documents have been provided, please upload these to the member’s internal documents.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card type="primary" className={classes.pipChangeContainer}>
      <Label type="darkGreenSmallBold">Proof-of-eligibility documents</Label>
      <p className={classes.pipChangeDescription}>
        Please make sure the policy will be eligible for the desired PIP Medical limit, and gather any new
        proof-of-eligibility documents from the member, if needed.
      </p>

      <div className={classes.linkSection}>
        <CardAnchorLink
          label="See PIP coverage options and eligibility requirements"
          onClick={goToPIPCoverageSection}
          icon={rightArrow}
          classes={classes}
          iconClass={classes.arrowIcon}
        />
        <CardAnchorLink
          label="See member's documents"
          icon={openInTab}
          classes={classes}
          onClick={openMemberDocuments}
        />
      </div>

      <NotificationCard type="secondary" icon={infoIcon} iconSize="icon16" className={classes.infoBox}>
        <div>
          Please send any new proof-of-eligibility documents to{' '}
          <Label
            component="a"
            href={`mailto:${brandConstants.SUPPORT_EMAIL}`}
            className={classes.emailLink}
            rel="noopener noreferrer"
          >
            {brandConstants.SUPPORT_EMAIL}
          </Label>{' '}
          with the subject line:{' '}
          <MUITooltip title="Click to copy to clipboard" arrow placement="top" classes={{ tooltip: classes.tooltip }}>
            <span onClick={copyEmailSubject} className={classes.emailLink}>
              Michigan PIP proof-of-eligibility documents for policy {id}
            </span>
          </MUITooltip>
        </div>
      </NotificationCard>
      <FormField
        mode="light"
        label="I have sent all required proof-of-eligibility documents to the email address provided above OR have verified that existing proof documents meet requirements."
        type="checkbox"
        id="proofOfElegibilityDocumentsAttestation"
        name="proofOfElegibilityDocumentsAttestation"
        ignoreGlobalDisabledState
        topAlignCheckbox
      />
    </Card>
  );
};

ProofOfElegibilityDocumentsChange.propTypes = {
  goToPIPCoverageSection: PropTypes.func.isRequired,
  openMemberDocuments: PropTypes.func.isRequired
};

export default observer(ProofOfElegibilityDocumentsChange);
